/**
 * The Slack turn worker composes each mention's user message as an optional
 * `<slack_thread_context channel="…" thread_ts="…">…</slack_thread_context>`
 * block (what was said in the Slack thread since the previous answer, one
 * attributed line per message) followed by the mention itself. See
 * `roboto_service.domain.slack.context` for the producer; this is the reader.
 *
 * Tolerant on purpose: anything that does not match the shape exactly is left
 * as ordinary text, so a malformed or hand-typed block can never hide a
 * message.
 */

export interface SlackThreadContext {
  channelId: string;
  threadTs: string;
  /** The block's lines after its preamble, in order: an optional "(N earlier messages omitted)" note, then one line per quoted message. */
  lines: string[];
}

export interface ParsedSlackMessage {
  context: SlackThreadContext | null;
  /** Everything after the block — the attributed mention line — or the whole text when there is no block. */
  rest: string;
}

const OPEN_TAG =
  /^<slack_thread_context channel="([^"]*)" thread_ts="([^"]*)">\n/;
const CLOSE_TAG = "\n</slack_thread_context>";

/**
 * Split a user message into its Slack context block (if any) and the rest.
 *
 * The block's first body line is a fixed preamble addressed to the model
 * ("Messages in this Slack thread since the previous answer…"); it is dropped
 * here because the disclosure's own label says the same thing to a person.
 */
export function parseSlackContext(text: string): ParsedSlackMessage {
  const open = OPEN_TAG.exec(text);
  if (open === null) {
    return { context: null, rest: text };
  }
  const bodyStart = open[0].length;
  const closeAt = text.indexOf(CLOSE_TAG, bodyStart);
  if (closeAt === -1) {
    return { context: null, rest: text };
  }
  const body = text.slice(bodyStart, closeAt);
  const [, ...lines] = body.split("\n");
  const rest = text.slice(closeAt + CLOSE_TAG.length).replace(/^\n+/, "");
  return {
    context: { channelId: open[1], threadTs: open[2], lines },
    rest,
  };
}
